import { ref as vueRef } from 'vue';
import { database, ref, onValue, set, onDisconnect, serverTimestamp } from '@/plugins/firebase';
import { useStore } from 'vuex';

/**
 * Firebase Realtime Presence System
 * Tracks active users in real-time using Firebase Realtime Database
 */
export function usePresence() {
  const store = useStore();
  const activeUsers = vueRef(0);
  const activeUsersList = vueRef([]);
  const isTracking = vueRef(false);
  let unsubscribe = null;

  /**
   * Start tracking user presence
   * Automatically sets user as online and handles disconnect
   */
  const startPresence = () => {
    const user = store.state.auth?.user;
    if (!user?.id || !database) {
      console.warn('Cannot start presence: User not authenticated or database not initialized');
      return;
    }

    try {
      const userStatusRef = ref(database, `status/${user.id}`);
      const connectedRef = ref(database, '.info/connected');

      // Listen to Firebase connection status
      onValue(connectedRef, (snapshot) => {
        if (snapshot.val() === true) {
          // User is connected
          const userData = {
            state: 'online',
            lastChanged: serverTimestamp(),
            userId: user.id,
            userName: user.name || user.email || 'Unknown User',
            email: user.email || '',
            tenantId: user.tenantId || '',
            tenantName: user.tenantName || '',
            isSuperAdmin: user.isSuperAdmin || false,
            country: user.country || '',
            city: user.city || ''
          };

          // Set user as online
          set(userStatusRef, userData);

          // Automatically set offline on disconnect
          onDisconnect(userStatusRef).set({
            ...userData,
            state: 'offline',
            lastChanged: serverTimestamp()
          });

          isTracking.value = true;
          console.log('✅ Presence tracking started for user:', user.email);
        }
      });
    } catch (error) {
      console.error('Error starting presence:', error);
    }
  };

  /**
   * Watch active users count in real-time
   * Updates whenever any user connects/disconnects
   */
  const watchActiveUsers = () => {
    if (!database) {
      console.warn('Database not initialized');
      return;
    }

    const statusRef = ref(database, 'status');
    
    unsubscribe = onValue(statusRef, (snapshot) => {
      let count = 0;
      const users = [];
      const now = Date.now();
      const ACTIVE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

      snapshot.forEach((childSnapshot) => {
        const status = childSnapshot.val();
        
        if (status && status.state === 'online') {
          // Consider user active if updated in last 5 minutes
          const lastChanged = status.lastChanged || 0;
          const isRecent = (now - lastChanged) < ACTIVE_THRESHOLD;
          
          if (isRecent || !status.lastChanged) { // Include if no timestamp (just connected)
            count++;
            users.push({
              userId: status.userId,
              userName: status.userName,
              email: status.email,
              tenantName: status.tenantName,
              lastChanged: status.lastChanged,
              isSuperAdmin: status.isSuperAdmin || false,
              country: status.country || '',
              city: status.city || ''
            });
          }
        }
      });

      activeUsers.value = count;
      activeUsersList.value = users;
      
      console.log('👥 Active users:', count);
    });
  };

  /**
   * Stop tracking presence (call on logout)
   */
  const stopPresence = () => {
    const user = store.state.auth?.user;
    if (!user?.id || !database) return;

    try {
      const userStatusRef = ref(database, `status/${user.id}`);
      
      // Set user as offline
      set(userStatusRef, {
        state: 'offline',
        lastChanged: serverTimestamp(),
        userId: user.id,
        userName: user.name || user.email,
        email: user.email || '',
        tenantId: user.tenantId || '',
        country: user.country || '',
        city: user.city || ''
      });

      isTracking.value = false;
      console.log('❌ Presence tracking stopped for user:', user.email);
    } catch (error) {
      console.error('Error stopping presence:', error);
    }
  };

  /**
   * Stop watching active users (cleanup)
   */
  const stopWatching = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };

  return {
    activeUsers,
    activeUsersList,
    isTracking,
    startPresence,
    watchActiveUsers,
    stopPresence,
    stopWatching
  };
}
